import { LogOut } from "lucide-react";
import { useEffect, useMemo, useState, type MouseEvent } from "react";
import Swal from "sweetalert2";

import type { User } from "../../types";
import type { ViewType } from "../../types/navigation";
import { displayRoles } from "../../utils/accessControl";
import { apiService } from "../../utils/apiService";
import { navigateToView, viewFromLocation } from "../../utils/appRoutes";
import { buildSidebarNavigation } from "../../utils/sidebarNavigation";
import { CompanyLogo } from "../ui/company-logo";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "../ui/sidebar";

interface AppSidebarProps {
  currentUser: User;
  onLogout: () => void;
}

function isPlainPrimaryClick(event: MouseEvent<HTMLAnchorElement>) {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

export function AppSidebar({ currentUser, onLogout }: AppSidebarProps) {
  const { isMobile, setOpenMobile, state } = useSidebar();
  const [activeView, setActiveView] = useState<ViewType>("dashboard");
  const isCollapsedDesktop = state === "collapsed" && !isMobile;
  const navigationGroups = useMemo(
    () => buildSidebarNavigation(currentUser),
    [currentUser],
  );
  const roleSummary = displayRoles(currentUser).join(" · ") || "Team member";
  const initials =
    currentUser.name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "U";

  useEffect(() => {
    const updateActiveView = () =>
      setActiveView(viewFromLocation(window.location) ?? "dashboard");
    updateActiveView();
    window.addEventListener("popstate", updateActiveView);
    return () => window.removeEventListener("popstate", updateActiveView);
  }, []);

  const performLogout = async () => {
    await apiService.logout();
    onLogout();
    window.location.assign("/");
  };

  const handleLogoutClick = async () => {
    const result = await Swal.fire({
      title: "Log out?",
      text: "You will need to sign in again to access your workspace.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Log out",
      cancelButtonText: "Stay signed in",
      focusCancel: true,
      customClass: {
        container: "swal-container",
        popup: "swal-popup",
        title: "swal-title",
        htmlContainer: "swal-content",
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
        icon: "swal-icon",
      },
    });

    if (result.isConfirmed) await performLogout();
  };

  return (
    <Sidebar
      className="h-dvh min-h-dvh border-r-0 text-white [&_[data-sidebar=sidebar]]:border-r [&_[data-sidebar=sidebar]]:border-white/10 [&_[data-sidebar=sidebar]]:bg-sidebar [&_[data-sidebar=sidebar]]:text-sidebar-foreground"
      collapsible="icon"
    >
      <SidebarHeader className="flex h-[73px] shrink-0 items-center border-b border-white/10 px-3 sm:h-[81px]">
        <CompanyLogo
          size="md"
          clickable
          compact={isCollapsedDesktop}
          variant="dark"
          className={isCollapsedDesktop ? "mx-auto justify-center" : "max-w-full"}
        />
      </SidebarHeader>

      <SidebarContent className="gap-0 overflow-x-hidden bg-transparent [scrollbar-color:rgba(255,255,255,0.22)_transparent] [scrollbar-width:thin]">
        {navigationGroups.map((group) => (
          <SidebarGroup key={group.title} className="px-2 py-2.5">
            <SidebarGroupLabel className="mb-1 h-6 px-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45 group-data-[collapsible=icon]:hidden">
              {group.title}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.view}>
                    <SidebarMenuButton
                      asChild
                      isActive={activeView === item.view}
                      className="app-sidebar-link h-10 rounded-lg border border-transparent bg-transparent p-0 text-white/68 transition-[background-color,color,border-color] duration-150 hover:bg-white/[0.08] hover:text-white data-[active=true]:border-white/10 data-[active=true]:bg-white/[0.12] data-[active=true]:font-medium data-[active=true]:text-white"
                      tooltip={isCollapsedDesktop ? item.title : undefined}
                    >
                      <a
                        href={`/${item.view}`}
                        className={
                          isCollapsedDesktop
                            ? "flex size-10 items-center justify-center"
                            : "flex h-10 w-full items-center gap-2.5 px-2.5"
                        }
                        aria-current={activeView === item.view ? "page" : undefined}
                        onClick={(event) => {
                          if (!isPlainPrimaryClick(event)) return;
                          event.preventDefault();
                          navigateToView(item.view);
                          if (isMobile) setOpenMobile(false);
                        }}
                      >
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-md text-current">
                          <item.icon aria-hidden="true" className="size-4 shrink-0" />
                        </span>
                        <span className={isCollapsedDesktop ? "sr-only" : "truncate"}>
                          {item.title}
                        </span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-white/10 p-2.5">
        <div
          className={
            isCollapsedDesktop
              ? "mb-1 flex justify-center"
              : "mb-2 flex min-w-0 items-center gap-2.5 rounded-lg border border-white/[0.08] bg-white/[0.04] px-2.5 py-2"
          }
          title={isCollapsedDesktop ? `${currentUser.name} — ${roleSummary}` : undefined}
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white text-xs font-bold text-sidebar">
            {initials}
          </span>
          {!isCollapsedDesktop && (
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-xs font-semibold text-white">
                {currentUser.name}
              </span>
              <span className="mt-0.5 block truncate text-[10px] text-white/45">
                {roleSummary}
              </span>
            </span>
          )}
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleLogoutClick}
              className="h-10 rounded-lg border border-transparent bg-transparent p-0 text-white/55 transition-[background-color,color] duration-150 hover:bg-white/[0.08] hover:text-white active:bg-white/[0.12]"
              tooltip={isCollapsedDesktop ? "Log out" : undefined}
              aria-label="Log out"
            >
              <span
                className={
                  isCollapsedDesktop
                    ? "flex size-10 items-center justify-center"
                    : "flex h-10 w-full items-center gap-2.5 px-2.5"
                }
              >
                <span className="flex size-7 shrink-0 items-center justify-center">
                  <LogOut aria-hidden="true" className="size-4" />
                </span>
                <span className={isCollapsedDesktop ? "sr-only" : ""}>Log out</span>
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
